import Navigation from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openCalendlyDemo } from "@/lib/analytics";
import {
  Database, 
  Network, 
  Shield, 
  CheckCircle,
  ArrowRight,
  Clock,
  Zap,
  Server,
  Globe,
  Lock,
  Activity,
  GitBranch,
  Layers,
  Settings,
  RefreshCw,
  Building2,
  Monitor,
  Workflow
} from 'lucide-react';

const NativeERP = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const integrationCapabilities = [
    {
      icon: <Database className="w-6 h-6 text-primary" />,
      title: "Real-Time Bi-Directional Sync",
      description: "Native API connections ensure your ERP and EZ Cloud are always perfectly synchronized with sub-100ms latency.",
      technical: "Event-driven architecture with CDC (Change Data Capture) for instantaneous updates"
    },
    {
      icon: <Shield className="w-6 h-6 text-primary" />,
      title: "Enterprise Security Framework",
      description: "Bank-grade encryption, VPN tunneling, and OAuth 2.0 with role-based access control for all integrations.",
      technical: "AES-256 encryption, mTLS certificates, SOC 2 Type II compliance with annual penetration testing"
    },
    {
      icon: <Workflow className="w-6 h-6 text-primary" />,
      title: "Zero-Downtime Implementation",
      description: "Parallel processing during setup ensures your current workflows continue uninterrupted during integration.",
      technical: "Blue-green deployment strategy with rollback capabilities and 99.99% uptime SLA"
    },
    {
      icon: <Activity className="w-6 h-6 text-primary" />,
      title: "Intelligent Error Handling",
      description: "Self-healing integration with automatic retry logic, circuit breakers, and proactive error detection.",
      technical: "Exponential backoff algorithms, health check endpoints, and comprehensive monitoring dashboard"
    }
  ];

  const erpPlatforms = [
    {
      name: "Oracle Cloud ERP",
      slug: "oracle",
      architecture: "REST APIs + Cloud Events",
      deploymentTime: "8-10 weeks",
      keyFeatures: [
        "Native Fusion Cloud integration",
        "Real-time invoice validation",
        "Advanced workflow routing",
        "Cloud security compliance"
      ],
      technicalSpecs: {
        "API Standard": "REST/JSON with OAuth 2.0",
        "Data Sync": "Real-time via webhooks",
        "Deployment": "Cloud-to-cloud native",
        "Monitoring": "Oracle Cloud Observability"
      }
    },
    {
      name: "Oracle EBS",
      slug: "oracle",
      architecture: "API Gateway + XML Processing",
      deploymentTime: "8-12 weeks",
      keyFeatures: [
        "Native E-Business Suite APIs",
        "Custom concurrent programs",
        "Duplicate detection via API",
        "Multi-org support"
      ],
      technicalSpecs: {
        "API Standard": "SOAP/XML with custom headers",
        "Data Sync": "Batch + real-time hybrid",
        "Deployment": "On-premise or hosted",
        "Monitoring": "Enterprise Manager integration"
      }
    },
    {
      name: "NetSuite",
      slug: "netsuite",
      architecture: "SuiteTalk REST + RESTlets",
      deploymentTime: "8-10 weeks",
      keyFeatures: [
        "OneWorld subsidiary routing",
        "SuiteFlow approval sync",
        "Vendor bill automation",
        "Project & class coding"
      ],
      technicalSpecs: {
        "API Standard": "REST + SOAP (SuiteTalk)",
        "Data Sync": "Real-time via REST",
        "Deployment": "Cloud-to-cloud native",
        "Monitoring": "SuiteAnalytics"
      }
    },
    {
      name: "SAP (ECC + S/4HANA)",
      slug: "sap",
      architecture: "OData + BAPI / IDoc",
      deploymentTime: "10-12 weeks",
      keyFeatures: [
        "MIRO replacement workflow",
        "Multi-company-code posting",
        "Tolerance-based 3-way match",
        "Cost center / WBS coding"
      ],
      technicalSpecs: {
        "API Standard": "OData / BAPI / IDoc",
        "Data Sync": "Real-time + batch",
        "Deployment": "ECC or S/4HANA",
        "Monitoring": "SAP Solution Manager"
      }
    },
    {
      name: "Microsoft Dynamics 365",
      slug: "dynamics",
      architecture: "Dataverse OData + Power Platform",
      deploymentTime: "8-12 weeks",
      keyFeatures: [
        "F&O + Business Central",
        "Legal-entity routing",
        "Intercompany processing",
        "Financial-dimension coding"
      ],
      technicalSpecs: {
        "API Standard": "OData / Dataverse Web API",
        "Data Sync": "Real-time",
        "Deployment": "F&O / Business Central",
        "Monitoring": "Lifecycle Services"
      }
    },
    {
      name: "Yardi Voyager",
      slug: "yardi",
      architecture: "SQL Integration + Web Services",
      deploymentTime: "8-10 weeks",
      keyFeatures: [
        "Property-specific GL coding",
        "Vendor management sync",
        "Multi-property processing",
        "Insurance certificate tracking"
      ],
      technicalSpecs: {
        "API Standard": "REST/SOAP hybrid",
        "Data Sync": "Real-time + scheduled batch",
        "Deployment": "Client-server environment",
        "Monitoring": "Yardi native reporting"
      }
    },
    {
      name: "Oracle PeopleSoft",
      slug: null,
      architecture: "Integration Broker + Component Interfaces",
      deploymentTime: "8-12 weeks",
      keyFeatures: [
        "Voucher build automation",
        "ChartField coding",
        "Multi-business-unit routing",
        "Approval Workflow Engine sync"
      ],
      technicalSpecs: {
        "API Standard": "Integration Broker (REST/SOAP)",
        "Data Sync": "Real-time + batch",
        "Deployment": "On-premise or hosted",
        "Monitoring": "PeopleSoft Health Center"
      }
    },
    {
      name: "Oracle JD Edwards",
      slug: null,
      architecture: "Orchestrator REST + AIS",
      deploymentTime: "8-12 weeks",
      keyFeatures: [
        "Voucher match automation",
        "Business-unit / object-account coding",
        "Multi-company processing",
        "Approval workflow sync"
      ],
      technicalSpecs: {
        "API Standard": "Orchestrator / AIS REST",
        "Data Sync": "Real-time + batch",
        "Deployment": "On-premise or OCI",
        "Monitoring": "JDE Server Manager"
      }
    },
  ];

  const dataFlowComponents = [
    {
      layer: "Authentication Layer",
      description: "Secure credential management and session handling",
      components: ["OAuth 2.0 Provider", "JWT Token Management", "Role-Based Access Control", "Session Persistence"]
    },
    {
      layer: "API Gateway",
      description: "Request routing, rate limiting, and protocol translation", 
      components: ["Load Balancer", "Rate Limiter", "Protocol Converter", "Request Logger"]
    },
    {
      layer: "Integration Engine",
      description: "Core business logic and data transformation",
      components: ["Business Rules Engine", "Data Mapper", "Validation Framework", "Error Handler"]
    },
    {
      layer: "Data Persistence",
      description: "Caching, queuing, and audit trail storage",
      components: ["Redis Cache", "Message Queue", "Audit Database", "Backup Systems"]
    }
  ];

  const performanceMetrics = [
    {
      metric: "99.99%",
      description: "Integration Uptime",
      detail: "Guaranteed SLA with monitoring"
    },
    {
      metric: "<100ms", 
      description: "API Response Time",
      detail: "Average ERP query response"
    },
    {
      metric: "8-12 Weeks",
      description: "Implementation Time",
      detail: "Typical go-live timeline"
    },
    {
      metric: "24/7",
      description: "Monitoring & Support",
      detail: "Proactive system monitoring"
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Native ERP Integration Architecture - Enterprise AP Automation | EZ Cloud"
        description="Enterprise-grade ERP integration with 99.99% uptime. Real-time APIs, zero-downtime deployment, and SOC 2 compliance for Oracle EBS + Fusion, NetSuite, SAP, Microsoft Dynamics, Yardi, PeopleSoft, and JDE."
        canonical="https://ezcloud.ai/technology/native-erp"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Platform', url: 'https://ezcloud.ai/#platform' },
          { name: 'Native ERP Integration', url: 'https://ezcloud.ai/technology/native-erp' },
        ]}
      />
      <Navigation />
      <Breadcrumbs />
      
      {/* Hero Section */}
      <section className="pt-20 pb-20 bg-hero-gradient text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-primary/10"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-accent/20 rounded-full blur-3xl -translate-x-48 -translate-y-48"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary/20 rounded-full blur-3xl translate-x-48 translate-y-48"></div>
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-accent text-accent-foreground px-6 py-2 text-lg">Enterprise Architecture</Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Native ERP Integration
              <span className="block text-accent">Architecture</span>
            </h1>
            <p className="text-xl lg:text-2xl mb-8 text-white/90 max-w-3xl mx-auto">
              Deep technical dive into our enterprise-grade ERP integration platform. 
              Real-time APIs, zero-downtime deployment, and bulletproof reliability.
            </p>
            
            {/* Key Architecture Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              <div className="text-center">
                <div className="text-3xl font-bold text-accent mb-2">4</div>
                <div className="text-sm text-white/80">Core ERP Platforms</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-accent mb-2">99.99%</div>
                <div className="text-sm text-white/80">Uptime SLA</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-accent mb-2">Zero</div>
                <div className="text-sm text-white/80">Downtime Required</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-accent mb-2">SOC 2</div>
                <div className="text-sm text-white/80">Compliance</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                variant="secondary" 
                className="text-lg px-8 py-4 bg-accent text-accent-foreground hover:bg-accent/90"
                onClick={() => openCalendlyDemo()}
              >
                <Network className="w-5 h-5 mr-2" />
                View Architecture Demo  
              </Button>
              <Button asChild size="lg" variant="outline-on-dark" className="text-lg px-8 py-4">
                <Link to="/integrations">
                  See ERP Partners
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* System Architecture Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
              System Architecture Overview
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Enterprise-grade integration platform built for reliability, scalability, and security.
            </p>
          </div>

          {/* Integration Architecture Flow */}
          <div className="bg-section-gradient rounded-lg p-8">
            <h3 className="text-2xl font-bold text-foreground mb-8 text-center">Integration Architecture Flow</h3>
            <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
              Enterprise-grade data pipeline with real-time processing, security, and error handling at every layer
            </p>
            
            <div className="relative">
              {/* Flow Steps */}
              <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 relative">
                {/* Step 1: API Gateway */}
                <div className="group relative">
                  <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-transparent group-hover:border-primary/20 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 h-52 flex flex-col">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-xl mb-4 mx-auto group-hover:bg-primary/20 transition-colors">
                      <Globe className="w-6 h-6 text-primary" />
                    </div>
                    <h4 className="text-lg font-bold text-center mb-2">API Gateway</h4>
                    <p className="text-sm text-muted-foreground text-center mb-4 flex-grow">Request routing & load balancing</p>
                    <div className="text-xs text-center text-primary font-medium mt-auto">~5ms</div>
                  </div>
                  {/* Connection Line */}
                  <div className="hidden lg:block absolute top-1/2 -right-2 w-4 h-0.5 bg-gradient-to-r from-primary to-accent transform -translate-y-1/2">
                    <div className="absolute -right-1 top-1/2 w-2 h-2 bg-primary rounded-full transform -translate-y-1/2"></div>
                  </div>
                </div>

                {/* Step 2: Authentication */}
                <div className="group relative">
                  <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-transparent group-hover:border-accent/20 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 h-52 flex flex-col">
                    <div className="flex items-center justify-center w-12 h-12 bg-accent/10 rounded-xl mb-4 mx-auto group-hover:bg-accent/20 transition-colors">
                      <Lock className="w-6 h-6 text-accent" />
                    </div>
                    <h4 className="text-lg font-bold text-center mb-2">Authentication</h4>
                    <p className="text-sm text-muted-foreground text-center mb-4 flex-grow">OAuth 2.0 & JWT validation</p>
                    <div className="text-xs text-center text-accent font-medium mt-auto">~15ms</div>
                  </div>
                  {/* Connection Line */}
                  <div className="hidden lg:block absolute top-1/2 -right-2 w-4 h-0.5 bg-gradient-to-r from-accent to-primary transform -translate-y-1/2">
                    <div className="absolute -right-1 top-1/2 w-2 h-2 bg-accent rounded-full transform -translate-y-1/2"></div>
                  </div>
                </div>

                {/* Step 3: Integration Engine */}
                <div className="group relative">
                  <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-transparent group-hover:border-primary/20 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 h-52 flex flex-col">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-xl mb-4 mx-auto group-hover:bg-primary/20 transition-colors">
                      <Settings className="w-6 h-6 text-primary" />
                    </div>
                    <h4 className="text-lg font-bold text-center mb-2">Integration Engine</h4>
                    <p className="text-sm text-muted-foreground text-center mb-4 flex-grow">Core business rules</p>
                    <div className="text-xs text-center text-primary font-medium mt-auto">~25ms</div>
                  </div>
                  {/* Connection Line */}
                  <div className="hidden lg:block absolute top-1/2 -right-2 w-4 h-0.5 bg-gradient-to-r from-primary to-accent transform -translate-y-1/2">
                    <div className="absolute -right-1 top-1/2 w-2 h-2 bg-primary rounded-full transform -translate-y-1/2"></div>
                  </div>
                </div>

                {/* Step 4: Business Logic */}
                <div className="group relative">
                  <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-transparent group-hover:border-accent/20 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 h-52 flex flex-col">
                    <div className="flex items-center justify-center w-12 h-12 bg-accent/10 rounded-xl mb-4 mx-auto group-hover:bg-accent/20 transition-colors">
                      <Workflow className="w-6 h-6 text-accent" />
                    </div>
                    <h4 className="text-lg font-bold text-center mb-2">Business Logic</h4>
                    <p className="text-sm text-muted-foreground text-center mb-4 flex-grow">Custom validation rules</p>
                    <div className="text-xs text-center text-accent font-medium mt-auto">~35ms</div>
                  </div>
                  {/* Connection Line */}
                  <div className="hidden lg:block absolute top-1/2 -right-2 w-4 h-0.5 bg-gradient-to-r from-accent to-secondary transform -translate-y-1/2">
                    <div className="absolute -right-1 top-1/2 w-2 h-2 bg-accent rounded-full transform -translate-y-1/2"></div>
                  </div>
                </div>

                {/* Step 5: Data Transformation */}
                <div className="group relative">
                  <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-transparent group-hover:border-primary/20 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 h-52 flex flex-col">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-xl mb-4 mx-auto group-hover:bg-primary/20 transition-colors">
                      <RefreshCw className="w-6 h-6 text-primary" />
                    </div>
                    <h4 className="text-lg font-bold text-center mb-2">Data Transform</h4>
                    <p className="text-sm text-muted-foreground text-center mb-4 flex-grow">Format conversion</p>
                    <div className="text-xs text-center text-primary font-medium mt-auto">~20ms</div>
                  </div>
                  {/* Connection Line */}
                  <div className="hidden lg:block absolute top-1/2 -right-2 w-4 h-0.5 bg-gradient-to-r from-secondary to-primary transform -translate-y-1/2">
                    <div className="absolute -right-1 top-1/2 w-2 h-2 bg-secondary rounded-full transform -translate-y-1/2"></div>
                  </div>
                </div>

                {/* Step 6: ERP Systems */}
                <div className="group relative">
                  <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-transparent group-hover:border-primary/20 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 h-52 flex flex-col">
                    <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-xl mb-4 mx-auto group-hover:scale-110 transition-transform">
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="text-lg font-bold text-center mb-2">ERP Systems</h4>
                    <p className="text-sm text-muted-foreground text-center mb-4 flex-grow">Oracle, Yardi, NetSuite</p>
                    <div className="text-xs text-center text-primary font-medium mt-auto">Variable</div>
                  </div>
                </div>
              </div>

              {/* Mobile Flow Connectors */}
              <div className="lg:hidden flex flex-col items-center space-y-4 absolute top-0 left-1/2 transform -translate-x-1/2 h-full">
                <div className="w-0.5 bg-gradient-to-b from-primary via-accent via-secondary to-primary h-full opacity-30"></div>
              </div>

              {/* Performance Stats */}
              <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center p-4 bg-white/50 rounded-lg">
                  <div className="text-2xl font-bold text-primary mb-1">~100ms</div>
                  <div className="text-xs text-muted-foreground">Total Round-Trip</div>
                </div>
                <div className="text-center p-4 bg-white/50 rounded-lg">
                  <div className="text-2xl font-bold text-accent mb-1">99.99%</div>
                  <div className="text-xs text-muted-foreground">Success Rate</div>
                </div>
                <div className="text-center p-4 bg-white/50 rounded-lg">
                  <div className="text-2xl font-bold text-primary mb-1">Real-Time</div>
                  <div className="text-xs text-muted-foreground">Bi-Directional Sync</div>
                </div>
                <div className="text-center p-4 bg-white/50 rounded-lg">
                  <div className="text-2xl font-bold text-primary mb-1">SOC 2</div>
                  <div className="text-xs text-muted-foreground">Security Certified</div>
                </div>
              </div>

              {/* Error Handling & Resilience */}
              <div className="mt-8 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-6">
                <h4 className="text-lg font-bold text-center mb-4">Enterprise Resilience Features</h4>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-2 mx-auto">
                      <Shield className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="text-sm font-medium">Circuit Breakers</div>
                    <div className="text-xs text-muted-foreground">Auto-failover protection</div>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-2 mx-auto">
                      <RefreshCw className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="text-sm font-medium">Retry Logic</div>
                    <div className="text-xs text-muted-foreground">Exponential backoff</div>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-2 mx-auto">
                      <Activity className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="text-sm font-medium">Health Monitoring</div>
                    <div className="text-xs text-muted-foreground">Real-time diagnostics</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Integration Capabilities */}
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {integrationCapabilities.map((capability, index) => (
              <Card key={index} className="border-none shadow-large hover:shadow-xl transition-spring bg-white">
                <CardHeader>
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="flex-shrink-0 p-3 bg-primary/10 rounded-lg">
                      {capability.icon}
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-foreground">{capability.title}</CardTitle>
                      <p className="text-muted-foreground mt-2">{capability.description}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-accent/10 rounded-lg p-4 border-l-4 border-accent">
                    <p className="text-sm text-muted-foreground">
                      <strong className="text-foreground">Technical Implementation:</strong> {capability.technical}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ERP Platform Specifications */}
      <section className="py-20 bg-section-gradient">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
              ERP Platform Specifications
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Deep integration specifications for each supported ERP platform with technical implementation details.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
            {erpPlatforms.map((erp, index) => (
              <Card key={index} className="border-none shadow-large hover:shadow-xl transition-spring bg-white">
                <CardHeader>
                  <div className="flex items-center justify-between mb-4">
                    <CardTitle className="text-2xl font-bold text-foreground">{erp.name}</CardTitle>
                    <Badge className="bg-primary text-primary-foreground">
                      {erp.deploymentTime}
                    </Badge>
                  </div>
                  <div className="flex items-center text-sm text-muted-foreground mb-4">
                    <Server className="w-4 h-4 mr-2" />
                    {erp.architecture}
                  </div>
                </CardHeader>

                <CardContent className="space-y-6">
                  {/* Key Features */}
                  <div>
                    <h4 className="font-semibold text-foreground mb-3 flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-accent" />
                      Integration Capabilities
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {erp.keyFeatures.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-start text-sm">
                          <div className="w-2 h-2 bg-accent rounded-full mt-2 mr-2 flex-shrink-0"></div>
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Technical Specifications */}
                  <div>
                    <h4 className="font-semibold text-foreground mb-3 flex items-center">
                      <Layers className="w-4 h-4 mr-2 text-primary" />
                      Technical Specifications
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      {Object.entries(erp.technicalSpecs).map(([key, value], specIndex) => (
                        <div key={specIndex} className="flex justify-between items-center py-1 text-sm">
                          <span className="font-medium text-foreground">{key}:</span>
                          <span className="text-muted-foreground text-right">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {erp.slug ? (
                    <Button variant="outline" className="w-full group hover:bg-accent hover:text-accent-foreground" asChild>
                      <Link to={`/integrations/${erp.slug}`}>
                        See the {erp.name} integration
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full group hover:bg-accent hover:text-accent-foreground"
                      onClick={() => openCalendlyDemo()}
                    >
                      Talk to us about {erp.name}
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Data Flow & Processing Layers */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
              Data Flow & Processing Layers
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Understanding the technical layers that ensure reliable, secure, and performant ERP integration.
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-8">
            {dataFlowComponents.map((layer, index) => (
              <Card key={index} className="border-none shadow-large bg-white overflow-hidden">
                <CardHeader className="bg-primary/5">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center text-white font-bold text-lg">
                      {index + 1}
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-foreground">{layer.layer}</CardTitle>
                      <p className="text-muted-foreground">{layer.description}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {layer.components.map((component, componentIndex) => (
                      <div key={componentIndex} className="bg-accent/10 rounded-lg p-3 text-center">
                        <div className="text-sm font-medium text-foreground">{component}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Performance Metrics & SLAs */}
      <section className="py-20 bg-section-gradient">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
              Performance Metrics & SLAs
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Enterprise-grade performance guarantees with comprehensive monitoring and support.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto mb-16">
            {performanceMetrics.map((metric, index) => (
              <Card key={index} className="border-none shadow-large bg-white text-center p-6">
                <div className="text-4xl font-bold text-accent mb-2">{metric.metric}</div>
                <div className="text-lg font-semibold text-foreground mb-2">{metric.description}</div>
                <div className="text-sm text-muted-foreground">{metric.detail}</div>
              </Card>
            ))}
          </div>

          {/* Security & Compliance */}
          <Card className="border-none shadow-xl bg-white max-w-5xl mx-auto">
            <CardHeader>
              <div className="text-center">
                <CardTitle className="text-2xl font-bold text-foreground flex items-center justify-center">
                  <Shield className="w-6 h-6 mr-2 text-accent" />
                  Security & Compliance Framework
                </CardTitle>
                <p className="text-muted-foreground mt-2">Enterprise security standards for all ERP integrations</p>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-accent" />
                  </div>
                  <h4 className="font-semibold text-foreground mb-2">Data Encryption</h4>
                  <p className="text-sm text-muted-foreground">AES-256 encryption at rest and in transit with TLS 1.3</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Monitor className="w-8 h-8 text-accent" />
                  </div>
                  <h4 className="font-semibold text-foreground mb-2">24/7 Monitoring</h4>
                  <p className="text-sm text-muted-foreground">Real-time system monitoring with proactive alerting</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Globe className="w-8 h-8 text-accent" />
                  </div>
                  <h4 className="font-semibold text-foreground mb-2">SOC 2 Compliance</h4>
                  <p className="text-sm text-muted-foreground">Annual audits with continuous compliance monitoring</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* RELATED RESOURCES */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3">
                Related EZ Cloud platform & ERP resources
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Where native ERP integration fits with AI Email Prevention, the multi-platform hub, and the specific ERP integrations it powers.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link
                to="/integrations/oracle"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Oracle EBS & Fusion
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Native AP integration with Oracle E-Business Suite and Oracle Fusion Cloud — multi-BU, project coding, SOX audit trail.
                </p>
              </Link>

              <Link
                to="/integrations/sap"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  SAP ECC & S/4HANA
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  MIRO replacement, multi-company-code routing, dimensional posting.
                </p>
              </Link>

              <Link
                to="/integrations/yardi"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Yardi Voyager
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Native Yardi Voyager AP — bi-directional sync, insurance certificates, ACH payment data, intercompany flows.
                </p>
              </Link>

              <Link
                to="/technology/ai-email-prevention"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  AI Email Prevention
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  The exception-prevention AI layer that runs above native ERP integration — resolves 90% of supplier issues automatically.
                </p>
              </Link>

              <Link
                to="/technology/multi-platform"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Multi-platform hub
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Unified AI dashboard across multiple ERPs and entities — the orchestration layer for multi-entity organizations.
                </p>
              </Link>

              <Link
                to="/customers/hacov"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  HACOV case study
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Native ERP integration in production — 71% faster AP, $79K annual savings on Yardi Voyager.
                </p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-hero-gradient text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready to Integrate Your ERP?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Get a detailed technical assessment of your ERP integration requirements. 
              Our team will design a custom integration architecture for your environment.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button 
                size="lg" 
                variant="secondary" 
                className="text-lg px-8 py-4 bg-accent text-accent-foreground hover:bg-accent/90"
                onClick={() => openCalendlyDemo()}
              >
                <Database className="w-5 h-5 mr-2" />
                Schedule Architecture Review
              </Button>
              <Button asChild size="lg" variant="outline-on-dark" className="text-lg px-8 py-4">
                <Link to="/technology/multi-platform">
                  Explore Platform Features
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
            
            <p className="text-sm text-white/70">
              🏗️ Enterprise architecture review • ⚡ Zero-downtime deployment • 🛡️ SOC 2 compliance included
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NativeERP;